import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { Services } from '@ibexfusion/core-utils';
import { Models } from '@ibexfusion/models';

import { PageAction } from '../../../components/page-actions/page-actions.component';
import { CompositionService } from '@services/compositions/composition.service';
import { CompositionTypeService } from '@services/composition-types/composition-type.service';
import { CompositionAppService } from '@services/compositions/composition-app.service';
import { DynamicContentEntranceService } from '@services/dynamic-content/dynamic-content-entrance.service';
import { OrganisationService } from '@services/organisations/organisation.service';
import { OrganisationCustomerService } from '@services/customers/organisation-customer.service';
import * as _ from 'lodash';

@Component({
  selector: 'composition-wizard',
  templateUrl: 'composition-wizard.component.html',
  styleUrls: ['composition-wizard.component.scss'],
  providers: [CompositionService, CompositionTypeService, CompositionAppService, DynamicContentEntranceService, OrganisationService, OrganisationCustomerService],
  standalone: false
})
export class CompositionWizardComponent implements OnInit {

  public title = 'Composition wizard';
  public pageActions: PageAction[] = [];
  public isLoading: boolean;
  public composition: Models.Composition;
  public compositionTypes: Models.CompositionType[];

  public isCopy = false;
  public organisations: Models.Organisation[];
  public orgCustomers: Models.OrganisationCustomer[];
  private copiedFrom;
  public organisationId;
  public customerId;

  constructor(
    private compositionService: CompositionService, private compositionTypeService: CompositionTypeService, private compositionAppService: CompositionAppService,
    private dynamicContentEntranceService: DynamicContentEntranceService, private orgService: OrganisationService, private orgCustomerService: OrganisationCustomerService,
    private router: Router, private activatedRoute: ActivatedRoute, private snackbar: SnackbarService
  ) {
  }

  async ngOnInit() {
    let compositionId = this.activatedRoute.snapshot.params['id'];
    const compositionTypeId = this.activatedRoute.snapshot.params['typeId'];
    this.isCopy = this.activatedRoute.snapshot.url[0].path === 'copy';
    this.createPageActions();
    if (compositionId) {
      this.title = 'Edit composition';
      const composition = await this.compositionService.get(compositionId);
      this.copiedFrom = JSON.parse(JSON.stringify(composition));
      this.composition = composition;
      if (this.isCopy) {
        this.title = 'Copy composition';
        delete this.composition._id;
        delete this.composition.title;
        compositionId = null;
        this.organisations = await this.getOrganisations();
        const customers = await this.orgCustomerService.find(0, 1000, { '$sort': { title: 1 } });
        this.orgCustomers = customers?.data;
      }
      this.title += ' ' + composition.title;
    } else {
      this.title = 'New composition';
      this.composition = new Models.Composition();
      this.composition.typeId = compositionTypeId;
    }
    const res = await this.compositionTypeService.find(0, 25);
    this.compositionTypes = res?.data;
  }

  public async saveComposition(reload?) {
    let composition;
    if (this.composition._id) {
      composition = await this.compositionService.update(this.composition._id, this.composition);
    } else {
      composition = await this.compositionService.create(this.composition);
    }
    if (!composition) {
      return;
    }

    if (this.isCopy) {
      const apps = await this.compositionAppService.find(0, 100, { compositionId: this.copiedFrom._id });
      for (let i = 0, p = Promise.resolve(); i < apps.data.length; i++) {
        const app = apps.data[i];
        delete app._id;
        app.compositionId = composition._id;
        await this.compositionAppService.create(app);
      }
      if (composition?.dynamicContentIds?.length) {
        for (let i = 0; i < composition.dynamicContentIds.length; i++) {
          const entrance = await this.dynamicContentEntranceService.get(composition.dynamicContentIds[i]);
          delete entrance._id;
          entrance.OwnerOrganisationID = Services.HashIdService.hashSingle(this.organisationId ? this.organisationId : entrance.OwnerOrganisationID);
          entrance.OwnerCustomerID = this.customerId ? this.customerId : entrance.OwnerCustomerID;
          entrance.children = [];
          entrance.categories = [];
          const newEntrance = await this.dynamicContentEntranceService.create(entrance);
          composition.dynamicContentIds[i] = newEntrance._id;
          return this.compositionService.update(composition._id, composition);
        }
      }
    }
    this.composition = composition;
    this.snackbar.success('Composition saved');
    if (reload) {
      this.router.navigate(['compositions', this.composition._id]);
    }
  }

  public setOrganisation(event: any) {
    this.organisationId = Services.HashIdService.unhashSingle(event.value);
  }

  public setCustomer(event: any) {
    this.customerId = event.value;
  }

  private async getOrganisations(skip = 0, organisations = []) {
    const result = await this.orgService.find(skip, 100);
    organisations = organisations.concat(result.data);
    if (result.data.length === 100) {
      return this.getOrganisations(skip + 100, organisations);
    } else {
      return organisations.slice().sort((a, b) => a.title.localeCompare(b.title));
    }
  }

  private createPageActions(): void {
    if (this.activatedRoute.snapshot.params['id']) {
      this.pageActions.push(new PageAction({
        icon: 'account_balance', title: 'Detail composition', callback: () => this.router.navigate(['compositions', this.activatedRoute.snapshot.params['id']])
      }));
    } else if (this.activatedRoute.snapshot.params['typeId']) {
      this.pageActions.push(new PageAction({
        icon: 'account_balance', title: 'Overview compositions', callback: () => this.router.navigate(['composition-types', this.activatedRoute.snapshot.params['typeId']])
      }));
    }
    this.pageActions.push(new PageAction({ icon: 'save', title: 'Save composition', type: 'action', callback: () => this.saveComposition() }));
  }
}
